import React from "react";
import PracticeBySection from "components/Dashboard/PracticeBySection";
import PracticeByTests from "components/Dashboard/PracticeByTests";
import SectionTests from "components/Dashboard/SectionTests";
import MockTestHistory from "components/Dashboard/MockTestHistory";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useLoading, useSnackbar } from "contexts";
import { useNavigate } from "react-router-dom";
import { getMockAttempts } from "services";
import { generateMock } from "services/sectionService";
import useIETLSStore from "utils/ieltsStore";
import Session from "utils/Session";

export default function Performance() {
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const loading = useLoading();
    const [selectedSection, setSelectedSection] = React.useState(null);

    const initIELTSTest = useIETLSStore(state => state.initTest);
    const setIELTSMock = useIETLSStore(state => state.setMock);

    const [hasAttempts, setHasAttempts] = React.useState(false);
    const [mockAttempts, setMockAttempts] = React.useState([]);

    React.useEffect(() => {
        let isMounted = true;

        (async () => {
            try {
                loading.show();
                const { mockAttempts: attempts = [] } = await getMockAttempts({
                    user: Session.userId, type: 'ielts', order: "desc", orderBy: "createdAt",
                    include: 'mockSections',
                });

                if (isMounted) {
                    setHasAttempts(attempts.length > 0);
                    setMockAttempts(attempts);
                }
            } catch (error) {
                console.error(error);
            } finally {
                if (isMounted) loading.hide();
            }
        })();

        return () => { isMounted = false; };
    }, []);

    const RAZORPAY = {
        section:   'https://rzp.io/rzp/3Qp9qObO',
        fullTest:  'https://rzp.io/rzp/LKEHKtW',
        unlimited: 'https://rzp.io/rzp/DJV8NEOR',
    };

    const handleTestAction = (action, card) => {
        if (action === "start_test") {
            const sections = card.sections || [];
            setIELTSMock({
                mockType: "ielts",
                sections,
                duration: sections.reduce((acc, s) => acc + (s.duration || 0), 0),
                completedSections: [],
            });
            navigate("/ielts/assessment-intro");
        } else if (action === "view_results" && card.attemptId) {
            navigate(`/ielts/report?aid=${card.attemptId}`);
        } else if (action === "free_prompt") {
            snackbar.info("Purchase a paid plan to attempt this.");
        } else if (action === "unlock") {
            window.location.href = RAZORPAY.fullTest;
        } else if (action === "unlock_section") {
            window.location.href = RAZORPAY.section;
        } else if (action === "unlock_premium") {
            window.location.href = RAZORPAY.unlimited;
        }
    };

    return (
        <>
            {/* Hero Banner */}
            <Box
                sx={{
                    backgroundColor: "#2E3A50",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    py: "72px",
                    px: "76px",
                }}
            >
                <Typography
                    sx={{
                        color: "#FFFFFF",
                        fontFamily: "Mulish, sans-serif",
                        fontWeight: 800,
                        fontSize: { xs: "1.9rem", md: "2.75rem" },
                        lineHeight: 1.2,
                        mb: "4px",
                    }}
                >
                    Practice All IELTS Sections with 
                </Typography>
                <Typography
                    sx={{
                        color: "#1E90FF",
                        fontFamily: "Mulish, sans-serif",
                        fontWeight: 800,
                        fontSize: { xs: "1.9rem", md: "2.75rem" },
                        lineHeight: 1.2,
                        mb: "24px",
                    }}
                >
                    AI Feedback
                </Typography>
                <Typography
                    sx={{
                        color: "rgba(255,255,255,0.65)",
                        fontFamily: "Mulish, sans-serif",
                        fontSize: "1rem",
                        lineHeight: 1.7,
                        maxWidth: "520px",
                        mb: "36px",
                    }}
                >
                    Practice all four IELTS sections with realistic tests and get instant,
                    actionable feedback to boost your band score.
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => document.getElementById('practice-by-tests')?.scrollIntoView({ behavior: 'smooth' })}
                    sx={{
                        backgroundColor: "#1E90FF",
                        color: "#FFFFFF",
                        borderRadius: "6px",
                        padding: "12px 28px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        fontFamily: "Mulish, sans-serif",
                        boxShadow: "none",
                        "&:hover": {
                            backgroundColor: "#1a7de0",
                            boxShadow: "none",
                        },
                    }}
                >
                    Start a Free IELTS Mock Test&nbsp;&nbsp;→
                </Button>
            </Box>

            {/* Practice by Tests — full-length templates with all four sections, always visible */}
            <PracticeByTests
                hasAttempts={hasAttempts}
                mockAttempts={mockAttempts}
                onAction={handleTestAction}
            />

            {/* Practice by Section / Section Tests swap */}
            {selectedSection ? (
                <SectionTests
                    section={selectedSection}
                    hasAttempts={hasAttempts}
                    mockAttempts={mockAttempts}
                    onBack={() => setSelectedSection(null)}
                    onAction={handleTestAction}
                />
            ) : (
                <PracticeBySection onSectionSelect={(section) => setSelectedSection(section)} />
            )}

            {/* Mock Test History */}
            <MockTestHistory
                onViewReport={(row) => navigate(`/ielts/report?aid=${row._id}`)}
                onContinue={async (row) => {
                    try {
                        loading.show();
                        const mock = await generateMock({ attemptId: row._id });
                        setIELTSMock(mock);
                        initIELTSTest({ attempt: row._id });
                        navigate(`/${row.type}/assessment`, { replace: true });
                    } catch (err) {
                        console.error(err);
                        snackbar.error(err.message || "Unable to resume test. Please try again.");
                    } finally {
                        loading.hide();
                    }
                }}
            />
        </>
    );
}
